// frontend/src/components/admin/FeatureFlags.jsx
// ============================================================================
// GESTION DES FEATURE FLAGS — THEME-AWARE (Light/Dark)
// Utilise le ThemeContext partagé depuis contexts/ThemeContext.js
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Zap, Globe, Building2, ChevronRight, CheckCircle2, XCircle,
  RefreshCw, Search, AlertCircle, ToggleLeft, ToggleRight,
  Shield, Code, Layers
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import FeatureManageModal from './FeatureManageModal';
import { useTheme } from '../../contexts/ThemeContext';

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

/** KPI mini tile */
const MiniKPI = ({ icon: Icon, value, label, accent, t }) => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200"
    style={{ backgroundColor: t.bgCard, borderColor: t.border }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = accent + '40'; e.currentTarget.style.boxShadow = t.shadow; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.boxShadow = 'none'; }}
  >
    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: accent + '12' }}>
      <Icon size={17} style={{ color: accent }} />
    </div>
    <div>
      <div className="text-xl font-extrabold leading-none tabular-nums" style={{ color: t.text }}>{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] mt-0.5" style={{ color: t.textMuted }}>{label}</div>
    </div>
  </div>
);

/** Progress bar */
const ProgressBar = ({ value, max, color, t }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: t.primarySofter }}>
        <div className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${Math.max(pct, value > 0 ? 3 : 0)}%`, backgroundColor: color, opacity: 0.8 }} />
      </div>
      <span className="text-[11px] font-bold tabular-nums min-w-[32px] text-right" style={{ color: t.textSecondary }}>
        {pct}%
      </span>
    </div>
  );
};

/** Feature card */
const FeatureCard = ({ feature, t, onToggleGlobal, onManage }) => {
  const isGlobal = feature.global_enabled === 1;
  const enabled = feature.companies_enabled || 0;
  const total = feature.total_companies || 0;
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    await onToggleGlobal(feature);
    setToggling(false);
  };

  return (
    <div className="rounded-xl border overflow-hidden transition-all duration-200"
      style={{ backgroundColor: t.bgCard, borderColor: t.border }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.borderActive + '50'; e.currentTarget.style.boxShadow = t.shadow; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div className="p-5">
        {/* Top row: title + global badge */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5 mb-1">
              <h3 className="text-[14px] font-bold truncate" style={{ color: t.text }}>
                {feature.feature_label || feature.feature_name}
              </h3>
              {/* Global / Selective badge */}
              {isGlobal ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0"
                  style={{ backgroundColor: t.successSoft, color: t.successText }}>
                  <Globe size={10} />
                  GLOBAL
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0"
                  style={{ backgroundColor: t.primarySoft, color: t.primaryText }}>
                  <Layers size={10} />
                  SÉLECTIF
                </span>
              )}
            </div>
            <code className="text-[11px] font-mono px-1.5 py-0.5 rounded"
              style={{ backgroundColor: t.primarySofter, color: t.textMuted }}>
              {feature.feature_name}
            </code>
          </div>
        </div>

        {/* Description */}
        {feature.feature_description && (
          <p className="text-[12px] font-medium leading-relaxed mb-3" style={{ color: t.textSecondary }}>
            {feature.feature_description}
          </p>
        )}

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold" style={{ color: t.textMuted }}>
              {enabled} / {total} entreprises
            </span>
          </div>
          <ProgressBar value={enabled} max={total} color={isGlobal ? t.successText : t.primary} t={t} />
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-2">
          {/* Toggle global */}
          <button onClick={handleToggle} disabled={toggling}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold border transition-all duration-200 disabled:opacity-40 active:scale-[0.97]"
            style={{
              backgroundColor: isGlobal ? t.dangerSoft : t.successSoft,
              borderColor: isGlobal ? t.dangerText + '20' : t.successText + '20',
              color: isGlobal ? t.dangerText : t.successText,
            }}
            onMouseEnter={(e) => {
              const c = isGlobal ? t.danger : t.success;
              e.currentTarget.style.backgroundColor = c;
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.borderColor = c;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isGlobal ? t.dangerSoft : t.successSoft;
              e.currentTarget.style.color = isGlobal ? t.dangerText : t.successText;
              e.currentTarget.style.borderColor = isGlobal ? t.dangerText + '20' : t.successText + '20';
            }}
          >
            {toggling ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : isGlobal ? (
              <ToggleRight size={13} />
            ) : (
              <ToggleLeft size={13} />
            )}
            {isGlobal ? 'Désactiver global' : 'Activer global'}
          </button>

          {/* Manage per company */}
          <button onClick={() => onManage(feature)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold border transition-all duration-200 active:scale-[0.97] ml-auto"
            style={{
              backgroundColor: t.primarySoft,
              borderColor: t.primaryBorder,
              color: t.primaryText,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = t.primary;
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.borderColor = t.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = t.primarySoft;
              e.currentTarget.style.color = t.primaryText;
              e.currentTarget.style.borderColor = t.primaryBorder;
            }}
          >
            <Building2 size={13} />
            Par entreprise
            <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const FeatureFlags = () => {
  const t = useTheme();

  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);

  // ── Load ──
  useEffect(() => { loadFeatures(); }, []);

  const loadFeatures = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const response = await superAdminApi.features.getAll();
      setFeatures(response.data || []);
    } catch (error) {
      console.error('[FeatureFlags] Load error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // ── Toggle global ──
  const handleToggleGlobal = useCallback(async (feature) => {
    const action = feature.global_enabled === 1 ? 'deactivateGlobal' : 'activateGlobal';
    try {
      await superAdminApi.features[action](feature.feature_name);
      await loadFeatures(true);
    } catch (error) {
      console.error('[FeatureFlags] Toggle error:', error);
    }
  }, [loadFeatures]);

  // ── Manage ──
  const handleManage = useCallback((feature) => {
    setSelectedFeature(feature);
    setShowManageModal(true);
  }, []);

  // ── Filter ──
  const filtered = useMemo(() => {
    if (!searchTerm) return features;
    const q = searchTerm.toLowerCase();
    return features.filter(f =>
      f.feature_name?.toLowerCase().includes(q) ||
      f.feature_label?.toLowerCase().includes(q)
    );
  }, [features, searchTerm]);

  // ── Stats ──
  const stats = useMemo(() => ({
    total: features.length,
    global: features.filter(f => f.global_enabled === 1).length,
    activations: features.reduce((s, f) => s + (f.companies_enabled || 0), 0),
  }), [features]);

  // ==========================================================================
  // LOADING
  // ==========================================================================
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative w-10 h-10 mb-4">
          <div className="absolute inset-0 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: t.border, borderTopColor: t.primary }} />
        </div>
        <span className="text-sm font-medium" style={{ color: t.textMuted }}>Chargement des features…</span>
      </div>
    );
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div className="space-y-5" style={{ animation: 'ffFadeIn 0.2s ease-out' }}>

      {/* ── Header ── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <h2 className="text-lg font-bold tracking-tight" style={{ color: t.text }}>Feature Flags</h2>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold"
              style={{ backgroundColor: t.primarySoft, color: t.primaryText }}>
              <Zap size={11} />
              Déploiement progressif
            </span>
          </div>
          <p className="text-sm font-medium" style={{ color: t.textSecondary }}>
            Activation et gestion des fonctionnalités par entreprise
          </p>
        </div>
        <button onClick={() => loadFeatures(true)} disabled={refreshing}
          className="p-2 rounded-lg transition-colors disabled:opacity-40"
          style={{ color: t.textMuted }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySofter}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          title="Actualiser">
          <RefreshCw size={17} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MiniKPI icon={Zap} value={stats.total} label="Features" accent={t.primary} t={t} />
        <MiniKPI icon={Globe} value={stats.global} label="Globales" accent={t.successText} t={t} />
        <MiniKPI icon={Building2} value={stats.activations} label="Activations" accent={t.warningText} t={t} />
      </div>

      {/* ── Search ── */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors"
          size={16} style={{ color: t.textMuted }} />
        <input
          type="text"
          placeholder="Rechercher une feature…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm font-medium border outline-none transition-all duration-200"
          style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.text }}
          onFocus={(e) => {
            e.target.style.backgroundColor = t.bgInputFocus;
            e.target.style.borderColor = t.borderActive;
            e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`;
          }}
          onBlur={(e) => {
            e.target.style.backgroundColor = t.bgInput;
            e.target.style.borderColor = t.border;
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>

      {/* ── Feature list ── */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 rounded-xl border" style={{ backgroundColor: t.bgCard, borderColor: t.border }}>
          <AlertCircle size={32} className="mx-auto mb-3" style={{ color: t.textMuted, opacity: 0.4 }} />
          <p className="text-sm font-medium" style={{ color: t.textSecondary }}>Aucune feature trouvée</p>
          {searchTerm && (
            <p className="text-xs mt-1" style={{ color: t.textMuted }}>Essayez un autre terme de recherche</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((feature, i) => (
            <div key={feature.feature_name}
              style={{ animation: 'ffCardIn 0.25s ease-out both', animationDelay: `${i * 40}ms` }}>
              <FeatureCard
                feature={feature} t={t}
                onToggleGlobal={handleToggleGlobal}
                onManage={handleManage}
              />
            </div>
          ))}
        </div>
      )}

      {/* ── Modal ── */}
      {showManageModal && selectedFeature && (
        <FeatureManageModal
          feature={selectedFeature}
          onClose={() => { setShowManageModal(false); setSelectedFeature(null); }}
          onUpdate={() => loadFeatures(true)}
        />
      )}

      <style>{`
        @keyframes ffFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ffCardIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default FeatureFlags;